import { useCallback, useRef } from 'react';
import { showSuccess, showError } from 'src/utils/toast/toastUtil';

/**
 * 엑셀 붙여넣기 공통 훅
 * 
 * 주요 기능:
 * 1. 선택된 셀 위치부터 엑셀 데이터 붙여넣기
 * 2. 자동완성 기능 (품목 코드 등)
 * 3. 금액 자동 계산
 * 4. 필요한 경우 빈 행 자동 추가
 * 5. 데이터 히스토리 저장 (Ctrl+Z 되돌리기 지원)
 */

// 컬럼 매핑 타입 정의
export type ColumnMapping = Record<string, number>;

// 필드 매핑 타입 정의
export type FieldMapping<T> = Record<string, keyof T>;

// 자동완성 설정 타입
export interface AutoCompleteConfig<T> {
  searchField: keyof T;                    // 검색할 필드명
  searchFunction: (value: string) => Promise<any>; // 검색 함수
  mapping: Record<string, keyof T>;        // 검색 결과 → 필드 매핑
}

// 엑셀 붙여넣기 설정 타입
export interface ExcelPasteConfig<T> {
  columnMapping: ColumnMapping;            // 컬럼 순서 매핑
  fieldMappings: FieldMapping<T>;          // 컬럼명 → 필드명 매핑
  autoCompleteFields?: AutoCompleteConfig<T>; // 자동완성 설정
  calculateAmount?: (data: T) => T;        // 금액 계산 함수
  defaultData: T;                          // 기본 데이터
  moduleName?: string;                     // 모듈명 (로그용)
}

// 붙여넣기 위치 정보 타입
export interface PastePosition {
  startRowIndex: number;
  startColIndex: number;
  focusedColId?: string;
}

/**
 * 엑셀 붙여넣기 공통 훅
 * 
 * @param config - 엑셀 붙여넣기 설정
 * @param dataList - 현재 데이터 목록
 * @param setDataList - 데이터 목록 업데이트 함수
 * @param saveHistoryRef - 히스토리 저장 함수 ref (선택사항)
 * @param gridRef - 그리드 ref (선택사항)
 */
export const useExcelPaste = <T extends Record<string, any>>(
  config: ExcelPasteConfig<T>,
  dataList: T[],
  setDataList: (data: T[]) => void,
  saveHistoryRef?: React.MutableRefObject<((data: T[]) => void) | null>,
  gridRef?: React.RefObject<any>
) => {
  const isPastingRef = useRef(false);

  /**
   * 컬럼 ID를 숫자 인덱스로 변환
   */
  const getColumnIndex = useCallback((colId: string): number => {
    return config.columnMapping[colId] || 0;
  }, [config.columnMapping]);

  /**
   * 컬럼 인덱스에 해당하는 필드명 찾기
   */
  const getFieldName = useCallback((colIndex: number): keyof T | null => {
    const fieldName = Object.keys(config.fieldMappings).find(
      key => config.columnMapping[key] === colIndex
    );
    return fieldName ? config.fieldMappings[fieldName] : null;
  }, [config.columnMapping, config.fieldMappings]);

  /**
   * 필요한 만큼 빈 행 추가
   */
  const addEmptyRows = useCallback((
    currentList: T[],
    startRowIndex: number,
    requiredRows: number
  ): T[] => {
    const currentRows = currentList.length;
    const rowsToAdd = requiredRows - currentRows;
    
    if (rowsToAdd <= 0) return currentList;

    const newEmptyRows: T[] = [];
    for (let i = 0; i < rowsToAdd; i++) {
      const newRow = {
        ...config.defaultData,
        valueIndex: Math.max(0, ...currentList.map(item => (item as any).valueIndex || 0)) + i + 1,
        orderIndex: currentList.length + i + 1,
        orderIndexSeq: 0,
        inserted: true,
        isModified: false,
      } as T;
      newEmptyRows.push(newRow);
    }

    console.log(`📋 ${config.moduleName || 'Excel'}: ${rowsToAdd}개 빈 행 추가`);
    return [...currentList, ...newEmptyRows];
  }, [config.defaultData, config.moduleName]);

  /**
   * 자동완성 실행
   */
  const executeAutoComplete = useCallback(async (
    targetRow: T,
    cellValue: string
  ): Promise<void> => {
    if (!config.autoCompleteFields || !cellValue?.trim()) return;

    try {
      const searchResult = await config.autoCompleteFields.searchFunction(cellValue.trim());
      if (searchResult) {
        // 검색 결과를 해당 필드에 매핑
        Object.entries(config.autoCompleteFields.mapping).forEach(([searchKey, targetField]) => {
          if (searchResult[searchKey] !== undefined) {
            (targetRow as any)[targetField] = searchResult[searchKey];
          }
        });
        
        console.log(`🔍 ${config.moduleName || 'Excel'} 자동완성: ${cellValue} → ${searchResult[Object.keys(config.autoCompleteFields.mapping)[0]]}`);
      }
    } catch (error) {
      console.warn(`${config.moduleName || 'Excel'} 자동완성 실패: ${cellValue}`, error);
    }
  }, [config.autoCompleteFields, config.moduleName]);

  /**
   * 셀 값 설정 및 처리
   */
  const setCellValue = useCallback(async (
    targetRow: T,
    fieldName: keyof T,
    cellValue: string
  ): Promise<void> => {
    // 기본 값 설정
    (targetRow as any)[fieldName] = cellValue;

    // 자동완성이 필요한 필드인지 확인
    if (config.autoCompleteFields && fieldName === config.autoCompleteFields.searchField) {
      await executeAutoComplete(targetRow, cellValue);
    }
  }, [config.autoCompleteFields, executeAutoComplete]);

  /**
   * 숫자 값 파싱 (콤마 제거)
   */
  const parseNumericValue = useCallback((value: string, defaultValue: number = 0): number => {
    return parseFloat(value?.replace(/,/g, '') || '') || defaultValue;
  }, []);

  /**
   * 메인 엑셀 붙여넣기 처리 함수
   */
  const handleExcelPaste = useCallback(async (
    parsedData: string[][],
    position?: PastePosition
  ): Promise<void> => {
    try {
      // 중복 실행 방지
      if (isPastingRef.current) return;
      isPastingRef.current = true;

      // 현재 데이터 복사
      const currentList = [...dataList];
      
      // 붙여넣기 위치 정보
      const startRowIndex = position?.startRowIndex || 0;
      const focusedColId = position?.focusedColId || '';
      const startColIndex = getColumnIndex(focusedColId);

      // 디버깅 로그
      console.log(`📋 ${config.moduleName || 'Excel'}: 붙여넣기 시작`, {
        startRowIndex,
        startColIndex,
        focusedColId,
        dataRows: parsedData.length,
        currentRows: currentList.length
      });

      // 히스토리 저장
      if (saveHistoryRef?.current) {
        saveHistoryRef.current(currentList);
      }

      // 필요한 행 수만큼 빈 행 추가
      const requiredRows = startRowIndex + parsedData.length;
      const updatedList = addEmptyRows(currentList, startRowIndex, requiredRows);

      // 각 행의 데이터 처리
      for (let rowIndex = 0; rowIndex < parsedData.length; rowIndex++) {
        const cells = parsedData[rowIndex];
        const targetRowIndex = startRowIndex + rowIndex;
        
        if (targetRowIndex < updatedList.length) {
          const targetRow = updatedList[targetRowIndex];
          
          // 각 셀의 데이터 설정
          for (let colIndex = 0; colIndex < cells.length; colIndex++) {
            const cellValue = cells[colIndex];
            const actualColIndex = startColIndex + colIndex;
            const fieldName = getFieldName(actualColIndex);
            
            if (fieldName && targetRow) {
              // 숫자 필드인지 확인하여 적절한 파싱 적용
              const numericFields = ['estimateQty', 'estimatePrice', 'dcAmt', 'itemAmt', 'qty', 'price', 'amount'];
              const isNumericField = numericFields.some(field => 
                fieldName.toString().toLowerCase().includes(field.toLowerCase())
              );
              
              if (isNumericField) {
                const numericValue = parseNumericValue(cellValue, fieldName.toString().includes('qty') ? 1 : 0);
                (targetRow as any)[fieldName] = numericValue;
              } else {
                await setCellValue(targetRow, fieldName, cellValue);
              }
            }
          }
          
          // 금액 자동 계산
          if (config.calculateAmount) {
            Object.assign(targetRow, config.calculateAmount(targetRow));
          }
        }
      }

      // 업데이트된 데이터 설정
      setDataList(updatedList);
      
      // 성공 메시지
      showSuccess(`📋 선택된 위치부터 ${parsedData.length}개 행이 붙여넣기되었습니다.`);
      
      // UI 업데이트
      setTimeout(() => {
        if (gridRef?.current?.api) {
          gridRef.current.api.ensureIndexVisible(startRowIndex, 'top');
        }
        isPastingRef.current = false;
      }, 100);

    } catch (error) {
      console.error(`${config.moduleName || 'Excel'} 붙여넣기 에러:`, error);
      showError("붙여넣기 중 오류가 발생했습니다.");
      isPastingRef.current = false;
    }
  }, [
    dataList, 
    setDataList, 
    config, 
    getColumnIndex, 
    getFieldName, 
    addEmptyRows, 
    setCellValue, 
    parseNumericValue,
    saveHistoryRef,
    gridRef
  ]);

  return {
    handleExcelPaste,
    isPasting: isPastingRef.current
  };
};
